"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  id: "all",
  label: "ALL"
}, {
  id: "structural",
  label: "STRUCTURAL"
}, {
  id: "finishes",
  label: "FINISHES"
}, {
  id: "mechanical",
  label: "MECHANICAL"
}, {
  id: "custom",
  label: "CUSTOM"
}];
const products = [{
  id: 1,
  category: "structural",
  eyebrow: "STRUCTURAL · STEEL",
  name: "Heavy Duty Steel Framing",
  nameItalic: "Framing",
  description: "Precision-engineered structural steel components designed for high-load industrial applications and durability.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/steel_framing_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/steel_framing_lifestyle.png",
  specs: "Grade: A992 Steel · Coating: Hot-dip galvanized · Tolerance: +/- 1mm · Certification: ASTM compliant · Lead time: 4-6 weeks."
}, {
  id: 2,
  category: "finishes",
  eyebrow: "FINISHES · ARCHITECTURAL",
  name: "Premium Cladding Panels",
  nameItalic: "Cladding",
  description: "High-performance architectural panels providing superior weather resistance and a modern aesthetic finish.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cladding_panels_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/cladding_panels_lifestyle.png",
  specs: "Material: Aluminum composite · Thickness: 4mm · Fire rating: Class A · Finish: PVDF coating · Custom colors available."
}, {
  id: 3,
  category: "mechanical",
  eyebrow: "MECHANICAL · HVAC",
  name: "Industrial Ventilation Units",
  nameItalic: "Ventilation",
  description: "Robust ventilation systems engineered for maximum airflow efficiency in large-scale commercial environments.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/ventilation_unit_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/ventilation_unit_lifestyle.png",
  specs: "Capacity: 5000 CFM · Motor: High-efficiency EC · Housing: Powder-coated steel · Noise level: <65dB · Energy Star certified."
}, {
  id: 4,
  category: "structural",
  eyebrow: "STRUCTURAL · CONCRETE",
  name: "Reinforced Concrete Beams",
  nameItalic: "Beams",
  description: "Pre-cast reinforced concrete beams built to withstand extreme environmental stress and heavy structural loads.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/concrete_beams_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/concrete_beams_lifestyle.png",
  specs: "Strength: 6000 PSI · Reinforcement: Grade 60 rebar · Length: Custom up to 40ft · Finish: Smooth formwork · Seismic rated."
}, {
  id: 5,
  category: "finishes",
  eyebrow: "FINISHES · GLAZING",
  name: "Thermal Glazing Systems",
  nameItalic: "Glazing",
  description: "Advanced thermal-break glazing systems designed to optimize building energy efficiency and natural light.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/glazing_system_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/glazing_system_lifestyle.png",
  specs: "U-Value: 0.28 · Glass: Double-pane low-E · Frame: Thermal-break aluminum · Sound reduction: 35 STC · Warranty: 10 years."
}, {
  id: 6,
  category: "custom",
  eyebrow: "CUSTOM · FABRICATION",
  name: "Bespoke Metal Assemblies",
  nameItalic: "Assemblies",
  description: "Tailor-made metal assemblies fabricated to exact project specifications for unique architectural requirements.",
  price: "QUOTE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_assembly_front.png",
  imageHover: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_assembly_lifestyle.png",
  specs: "Material: Stainless/Carbon steel · Process: CNC laser cutting · Welding: AWS certified · Finish: Powder coat or anodized · CAD support included."
}];
export default function AvailableSubmittalPackages() {
  const [activeCategory, setActiveCategory] = useState("all");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const filtered = activeCategory === "all" ? products : products.filter(p => p.category === activeCategory);
  return <section data-section-id="2749" id="available-submittal-packages" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase mb-8" style={{
          color: "var(--primary)"
        }}>
            Available Submittal Packages
          </p>
          <h2 className="font-default font-light text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.1] max-w-[900px] mx-auto text-foreground">
            Engineered <em className="italic font-light">solutions</em> for your next project.
          </h2>
        </div>

        <div className="mt-20 md:mt-24 flex justify-center">
          <ToggleGroup type="single" value={activeCategory} onValueChange={v => v && setActiveCategory(v)} className="flex flex-wrap justify-center gap-2 bg-transparent">
            {categories.map((cat, i) => <ToggleGroupItem data-index={i} key={cat.id} value={cat.id} className="font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 h-auto rounded-lg border border-border bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary first:rounded-l-lg last:rounded-r-lg">
                {cat.label}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {filtered.map((product, i) => <ProductCard data-index={i} key={product.id} product={product} />)}
        </div>
      </div>
    </section>;
}
function ProductCard({
  product,
  ...rest
}: {
  product: (typeof products)[number];
  [key: string]: any;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <Card className="group bg-card text-card-foreground rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-500 overflow-hidden border-0 py-0 gap-0">
        <div className="p-5">
          <div className="relative overflow-hidden rounded-xl bg-muted">
            <AspectRatio ratio={4 / 5}>
              <img src={product.image} data-wvc-srcset={`${product.image}-wvc-srcset`} data-wvc-sizes={`${product.image}-wvc-sizes`} alt={product.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[400ms] group-hover:opacity-0" />
              <img src={product.imageHover} data-wvc-srcset={`${product.imageHover}-wvc-srcset`} data-wvc-sizes={`${product.imageHover}-wvc-sizes`} alt={`${product.name} lifestyle`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-[400ms] group-hover:opacity-100" />
            </AspectRatio>
          </div>
        </div>

        <CardContent className="px-7 pb-7 pt-2">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-4" style={{
          color: "var(--primary)"
        }}>
            {product.eyebrow}
          </p>
          <h3 className="font-default text-2xl font-normal text-foreground leading-tight mb-3">
            {product.name}
          </h3>
          <p className="font-default italic text-base leading-relaxed text-foreground/70 mb-8 min-h-[3rem]">
            {product.description}
          </p>

          <div className="flex items-center justify-between pt-2">
            <span className="font-default text-2xl text-foreground">
              {product.price}
            </span>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" className="group/btn flex items-center gap-3 px-0 hover:bg-transparent">
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase" style={{
                  color: "var(--primary)"
                }}>
                    Details
                  </span>
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg transition-transform duration-300 group-hover/btn:translate-x-1" style={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)"
                }}>
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card text-card-foreground border-border rounded-2xl">
                <DialogHeader>
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-2" style={{
                  color: "var(--primary)"
                }}>
                    {product.eyebrow}
                  </p>
                  <DialogTitle className="font-default text-3xl font-light text-foreground">
                    {product.name}
                  </DialogTitle>
                  <DialogDescription className="font-default italic text-base text-foreground/70 leading-relaxed pt-2">
                    {product.description}
                  </DialogDescription>
                </DialogHeader>

                <div className="border-t border-border pt-5 mt-2">
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase mb-3" style={{
                  color: "var(--primary)"
                }}>
                    Specifications
                  </p>
                  <p className="font-default text-sm text-foreground/80 leading-relaxed">
                    {product.specs}
                  </p>
                </div>

                <DialogFooter className="flex items-center sm:justify-between gap-4 pt-4 border-t border-border">
                  <span className="font-default text-2xl text-foreground">
                    {product.price}
                  </span>
                  <Button asChild className="rounded-lg px-6" style={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)"
                }}>
                    <Link to="/ContactPage">
                      <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
                        Inquire
                      </span>
                    </Link>
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>;
}